import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarX, Clock, Home, MapPin, Navigation, ShieldCheck, Sunrise } from 'lucide-react';
import type { QueueAccessSettings } from '../../types';
import { queueAccessService } from '../../services/queueAccessService';
import '../../styles/captcha.css';

interface QueueAccessSettingsPanelProps {
  onNotify: (message: string, type: 'success' | 'error') => void;
}

type TimeKey = 'captchaFreeFrom' | 'shift1AnyLocationFrom';
type SettingKey = Exclude<keyof QueueAccessSettings, TimeKey>;

const SETTINGS: { key: SettingKey; icon: typeof MapPin }[] = [
  { key: 'locationCheckEnabled', icon: MapPin },
  { key: 'captchaEnabled', icon: ShieldCheck },
  { key: 'skipDayAfterChargingEnabled', icon: CalendarX },
  // Owner-only: sent by the server only to the owner, so only shown to them
  { key: 'homeLocationEnabled', icon: Home },
  { key: 'timeLimitsEnabled', icon: Clock },
];

/** Time settings shown under their switch while it's on. */
const TIME_SETTINGS: { key: TimeKey; under: SettingKey; icon: typeof MapPin }[] = [
  { key: 'shift1AnyLocationFrom', under: 'locationCheckEnabled', icon: Navigation },
  { key: 'captchaFreeFrom', under: 'captchaEnabled', icon: Sunrise },
];

/** Admin switches for the queue join rules. Each change is saved immediately. */
export function QueueAccessSettingsPanel({ onNotify }: QueueAccessSettingsPanelProps) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<QueueAccessSettings | null>(null);
  const [saving, setSaving] = useState<SettingKey | TimeKey | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    queueAccessService
      .getAdminSettings()
      .then(setSettings)
      .catch(() => setLoadFailed(true));
  }, []);

  const save = async (key: SettingKey | TimeKey, next: QueueAccessSettings) => {
    if (saving) return;
    setSaving(key);
    try {
      setSettings(await queueAccessService.updateAdminSettings(next));
      onNotify(t('admin.settings.saved'), 'success');
    } catch {
      onNotify(t('admin.settings.saveFailed'), 'error');
    } finally {
      setSaving(null);
    }
  };

  const toggle = (key: SettingKey) => {
    if (!settings) return;
    save(key, { ...settings, [key]: !settings[key] });
  };

  const changeTime = (key: TimeKey, value: string) => {
    if (!settings || !/^\d{2}:\d{2}$/.test(value) || value === settings[key]) return;
    save(key, { ...settings, [key]: value });
  };

  return (
    <div className="modern-admin-section">
      <div className="modern-section">
        <div className="section-header">
          <h3 className="section-title">{t('admin.settings.queueAccessTitle')}</h3>
        </div>
        <p className="admin-settings-intro">{t('admin.settings.queueAccessIntro')}</p>

        {loadFailed && <div className="modern-error-inline">{t('admin.settings.loadFailed')}</div>}

        {settings && (
          <div className="admin-settings-list">
            {SETTINGS.filter(({ key }) => typeof settings[key] === 'boolean').map(({ key, icon: Icon }) => (
              <Fragment key={key}>
                <div className="admin-setting-row">
                  <Icon size={20} className="admin-setting-icon" />
                  <div className="admin-setting-text">
                    <span className="admin-setting-label" id={`setting-${key}`}>
                      {t(`admin.settings.${key}.label`)}
                    </span>
                    <span className="admin-setting-description">
                      {t(`admin.settings.${key}.${settings[key] ? 'onDescription' : 'offDescription'}`)}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={settings[key]}
                    aria-labelledby={`setting-${key}`}
                    className={`admin-switch ${settings[key] ? 'on' : ''}`}
                    disabled={saving !== null}
                    onClick={() => toggle(key)}
                  >
                    <span className="admin-switch-thumb" />
                  </button>
                </div>

                {TIME_SETTINGS.filter(time => time.under === key && settings[key] && typeof settings[time.key] === 'string').map(
                  ({ key: timeKey, icon: TimeIcon }) => (
                    <div key={timeKey} className="admin-setting-row admin-setting-subrow">
                      <TimeIcon size={20} className="admin-setting-icon" />
                      <div className="admin-setting-text">
                        <label className="admin-setting-label" htmlFor={`setting-${timeKey}`}>
                          {t(`admin.settings.${timeKey}.label`)}
                        </label>
                        <span className="admin-setting-description">
                          {t(`admin.settings.${timeKey}.description`, { time: settings[timeKey] })}
                        </span>
                      </div>
                      <input
                        id={`setting-${timeKey}`}
                        type="time"
                        step={60}
                        max="18:59"
                        className="admin-time-input"
                        defaultValue={settings[timeKey]}
                        key={settings[timeKey]}
                        disabled={saving !== null}
                        onBlur={e => changeTime(timeKey, e.currentTarget.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') changeTime(timeKey, e.currentTarget.value);
                        }}
                      />
                    </div>
                  )
                )}
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
